import React from "react";
import { Container, Col, Row, Modal, Button } from "react-bootstrap";
import { Link, useHistory } from "react-router-dom";
import { BackNavRow, PageContainer } from "../common/CommonStyles";
import { useTranslation } from "react-i18next";
import { useLiveData } from "../common/utils";
import {
  AnalyticsContext,
  ManageAccountDeleteAccountTapEvent,
  ManageAccountLogOutTapEvent,
  ManageAccountManageSubscriptionTapEvent,
  ViewManageAccountEvent,
} from "../common/context/AnalyticsContext";
import { Account } from "../common/context/AccountContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faStarCircle } from "@fortawesome/pro-solid-svg-icons";
import { AccountContext } from "../common/context/AccountContext";
import { ApiContext } from "../common/context/ApiContext";
import { isInTrialMode, isUserExpired, isUserSignedUp } from "../common/api";
import { COLOR_RED_BRIGHT } from "../common/colors";

export const ManageAccountPage: React.FC = () => {
  const { t } = useTranslation();
  React.useState<Boolean>(false);
  const { logEvent } = React.useContext(AnalyticsContext);
  const { accountLiveData, logout } = React.useContext(AccountContext);
  const [userEmail, setUserEmail] = React.useState<string>("");
  const [canAccessManageSubscription, setCanAccessManageSubscription] =
    React.useState<boolean>();
  const [userIsSignedUp, setUserIsSignedUp] = React.useState<boolean>(false);
  const { history } = useHistory();
  const { getManageSubscriptionUrl, deleteAccount } =
    React.useContext(ApiContext);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [showErrorModal, setShowErrorModal] = React.useState<boolean>(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] =
    React.useState<boolean>(false);

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  const doDeleteAccount = React.useCallback(async () => {
    deleteAccount().then((response) => {
      setShowDeleteAccountModal(false);
      if (response.result === "ok") {
        logout();
        history.push("/");
      } else {
        setErrorMessage(
          response.error_message || t("manage_account_delete_account_error")
        );
        setShowErrorModal(true);
      }
    });
  }, [
    t,
    deleteAccount,
    logout,
    history,
    setShowDeleteAccountModal,
    setErrorMessage,
    setShowErrorModal,
  ]);

  useLiveData(
    React.useCallback(
      (account: Account) => {
        if (account.hasUserAndMaybeProfile()) {
          setUserIsSignedUp(isUserSignedUp(account.user!!));
          setUserEmail(account.user!!.email || "");
          setCanAccessManageSubscription(
            !isInTrialMode(account.user!!) || isUserExpired(account.user!!)
          );
        }
      },
      [setUserEmail]
    ),
    accountLiveData
  );

  React.useEffect(() => {
    logEvent(new ViewManageAccountEvent());
  }, [logEvent]);

  if (!userIsSignedUp) {
    return <></>;
  }
  return (
    <PageContainer>
      <BackNavRow label={t("generic_nav_back")}>
        <h2>{t("manage_account_title")}</h2>
      </BackNavRow>
      <>
        <Row className="justify-content-center mt-4">
          <Col xs={12} lg={8} xl={6}>
            <div className="text-center">
              {t("logged_in_as", { email: userEmail })}
            </div>
            {/* Manage subscription */}
            {canAccessManageSubscription && (
              <Container className="mt-2 mb-4">
                <div className="border rounded overflow-hidden">
                  <Link
                    key={"manage-subscription-link"}
                    to=""
                    onClick={(event) => {
                      event.preventDefault();
                      logEvent(new ManageAccountManageSubscriptionTapEvent());
                      getManageSubscriptionUrl().then((response) => {
                        if (response.result === "ok") {
                          window.location.href = response.url!!;
                        } else {
                          setErrorMessage(response.error_message || "");
                          setShowErrorModal(true);
                        }
                      });
                    }}
                    style={{
                      display: "block",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <Row
                      key={"manage-account-row"}
                      style={{
                        marginLeft: 0,
                        marginRight: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        borderBottom: undefined,
                        marginInline: "0.5rem",
                      }}
                    >
                      <Col
                        xs="auto"
                        className="py-2"
                        style={{
                          borderLeft: "none",
                          borderRight: "none",
                        }}
                      >
                        <FontAwesomeIcon icon={faStarCircle} />
                      </Col>
                      <Col
                        className="py-2 px-3"
                        style={{
                          flexGrow: 1,
                          borderLeft: "none",
                          borderRight: "none",
                        }}
                      >
                        {t("manage_account_manage_subscription")}
                      </Col>
                      <Col
                        xs="auto"
                        className="py-2 px-3"
                        style={{ whiteSpace: "nowrap", borderLeft: "none" }}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </Col>
                    </Row>
                  </Link>
                </div>
              </Container>
            )}

            {/* Log out */}
            <Container className="mt-2 mb-4">
              <div className="border rounded overflow-hidden">
                <Link
                  key={"log-out-link"}
                  to=""
                  onClick={() => {
                    logEvent(new ManageAccountLogOutTapEvent());
                    logout();
                    history.push("/");
                  }}
                  style={{
                    display: "block",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <Row
                    key={"legal-row"}
                    style={{
                      marginLeft: 0,
                      marginRight: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                      borderBottom: undefined,
                      marginInline: "0.5rem",
                    }}
                  >
                    <Col
                      className="py-2 px-3"
                      style={{
                        flexGrow: 1,
                        borderLeft: "none",
                        borderRight: "none",
                        color: COLOR_RED_BRIGHT,
                        textAlign: "center",
                      }}
                    >
                      {t("navigation_logout")}
                    </Col>
                  </Row>
                </Link>
              </div>
            </Container>

            {/* Delete account */}
            <Container className="mt-2 mb-4">
              <div className="border rounded overflow-hidden">
                <Link
                  key={"delete-account-link"}
                  to=""
                  onClick={(e) => {
                    e.preventDefault();
                    logEvent(new ManageAccountDeleteAccountTapEvent());
                    setShowDeleteAccountModal(true);
                  }}
                  style={{
                    display: "block",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <Row
                    key={"delete-account-row"}
                    style={{
                      marginLeft: 0,
                      marginRight: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                      borderBottom: undefined,
                      marginInline: "0.5rem",
                    }}
                  >
                    <Col
                      className="py-2 px-3"
                      style={{
                        flexGrow: 1,
                        borderLeft: "none",
                        borderRight: "none",
                      }}
                    >
                      {t("manage_account_delete_account")}
                    </Col>
                  </Row>
                </Link>
              </div>
            </Container>
          </Col>
        </Row>
      </>

      {/* Error modal */}
      <Modal show={showErrorModal} onHide={closeErrorModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t("error_alert_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            className="text-decoration-none"
            onClick={closeErrorModal}
          >
            {t("generic_ok")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete account modal */}
      <Modal
        show={showDeleteAccountModal}
        onHide={() => {
          setShowDeleteAccountModal(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("manage_account_delete_account")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("manage_account_delete_account_confirmation_description")}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteAccountModal(false);
            }}
          >
            {t("generic_cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={(event) => {
              event.preventDefault();
              doDeleteAccount();
            }}
          >
            {t("generic_delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageContainer>
  );
};
